import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  SuggestionsContext,
  SuggestionsContextValue,
} from 'js/social/contexts/suggestions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSuggestionGroups } from 'js/social/api/suggested_api';

const SUGGESTIONS_QUERY_KEY = ['suggestions'];

type Props = {
  count: number;
  staleTime?: number;
  page?: string;
  render?: (value: SuggestionsContextValue) => ReactNode;
};

// order of categories to try.
// Keep in sync with log_carousel_category_stats in
// app/controllers/api/v0/suggestions/following_controller.rb
const CATEGORY_HIERARCHY = [
  'ImportedContacts',
  'AuthorsBookmarked',
  'OwnFollowers',
  'YouMayKnow',
];

export const SuggestionsProvider: React.FC<React.PropsWithChildren<Props>> = ({
  count,
  staleTime = Infinity,
  page,
  children,
  render,
}) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useQuery({
    staleTime,
    queryKey: SUGGESTIONS_QUERY_KEY,
    queryFn: async () => await getSuggestionGroups(count, page),
  });
  const groups = useMemo(() => data?.groups ?? [], [data?.groups]);

  const refresh = async () => {
    return await queryClient.invalidateQueries({
      queryKey: SUGGESTIONS_QUERY_KEY,
    });
  };

  const getGroup = (groupId: string | null) => {
    return groups.find((group) => group.id === groupId) ?? null;
  };

  const getRandomGroupId = useCallback(
    (minSuggestions: number) => {
      const options = groups
        .filter((group) => group.suggestions.length >= minSuggestions)
        .map((group) => group.id);

      if (options.length > 0) {
        // Get a random group from the available options
        return options[Math.floor(Math.random() * options.length)];
      }
      return null;
    },
    [groups],
  );

  const getSuggestions = useCallback(
    (groupId: string, count: number) => {
      const group = groups.find((group) => group.id === groupId);
      if (group) {
        return group.suggestions.slice(0, count);
      }
      return [];
    },
    [groups],
  );

  const getFirstPriorityGroupId = useCallback(
    (minSuggestions: number) => {
      const groupId = CATEGORY_HIERARCHY.find((groupId) => {
        return (
          getSuggestions(groupId, minSuggestions).length === minSuggestions
        );
      });

      return groupId || getRandomGroupId(minSuggestions);
    },
    [getSuggestions, getRandomGroupId],
  );

  const value = {
    groups,
    count: data?.count ?? 0,
    hasMore: data?.has_more ?? false,
    isLoading: isLoading || isFetching,
    refresh,
    getGroup,
    getRandomGroupId,
    getFirstPriorityGroupId,
    getSuggestions,
  };

  return (
    <SuggestionsContext.Provider value={value}>
      {render ? render(value) : children}
    </SuggestionsContext.Provider>
  );
};
